import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

interface UacAction {
  id: number;
  main_menu_id?: number;
  is_main: number;
  is_component: number;
  parent_id?: number | null;
  name: string;
  slug: string;
  action_url: string;
  is_active: number;
  created_by: number;
  updated_by: number;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
  sub_actions?: UacAction[];
}


@Injectable({
  providedIn: 'root'
})
export class authMenuGuard implements CanActivate {

  constructor(private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const menuSlug = route.data?.['menu_name'];
    const actionSlug = this.getActionSlugForRoute(route);
    const currentUrl = this.normalizePath(state.url);

    // Read menus from localStorage
    const storedMenus = localStorage.getItem('auth_data');
    let menus: [] = [];
    let slugGroups: { [parent_slug: string]: string[] } = {};
    try {
      const parsed = storedMenus ? JSON.parse(storedMenus) : [];
      menus = parsed?.permissions?.menu_tree || [];
      slugGroups = parsed?.permissions?.slug_array || {};
    } catch {
      this.router.navigate(['/unauthorized']);
      return false;
    }

    // Allow guarded action routes such as /performance/department/:id
    // even when the user does not have the fallback menu_name.
    if (actionSlug && this.isActionAllowed(actionSlug, slugGroups)) {
      return true;
    }

    // Check if menuSlug is allowed
    if (menuSlug && this.isMenuAllowed(menuSlug, currentUrl, menus)) {
      return true;
    }

    // Not allowed, redirect to unauthorized
    this.router.navigate(['/unauthorized']);
    return false;
  }

  private getActionSlugForRoute(route: ActivatedRouteSnapshot): string | null {
    const explicitActionSlug = route.data?.['action_slug'];
    if (explicitActionSlug) {
      return explicitActionSlug;
    }

    const actionSlugByType = route.data?.['action_slug_by_type'];
    const type = route.params?.['type'] || route.queryParams?.['type'];

    if (actionSlugByType && type) {
      return actionSlugByType[String(type).toLowerCase()] || null;
    }

    return null;
  }

  private isActionAllowed(targetSlug: string, slugGroups: { [parent_slug: string]: string[] }): boolean {
    const normalizedTargetSlug = this.normalizeText(targetSlug);
    const slugs = this.flattenSlugGroups(slugGroups);

    return slugs.some((slug: string) => this.normalizeText(slug) === normalizedTargetSlug);
  }

  private isMenuAllowed(targetSlug: string, currentUrl: string, menus: any[]): boolean {
    const normalizedTargetSlug = this.normalizeText(targetSlug);

    for (const menu of menus) {
      if (!menu) {
        continue;
      }

      const menuName = this.normalizeText(menu.menu_name);
      const menuSlug = this.normalizeText(menu.menu_slug);
      const menuUrl = this.normalizePath(menu.url);

      if (
        menuName === normalizedTargetSlug ||
        menuSlug === normalizedTargetSlug ||
        (menuUrl && currentUrl.startsWith(menuUrl))
      ) {
        return true;
      }

      if (menu.sub_menus?.length && this.isMenuAllowed(targetSlug, currentUrl, menu.sub_menus)) {
        return true;
      }
    }

    return false;
  }

  private flattenSlugGroups(slugGroups: { [parent_slug: string]: unknown }): string[] {
    return Object.values(slugGroups).flatMap((value: unknown) => {
      if (Array.isArray(value)) {
        return value.filter((slug): slug is string => typeof slug === 'string');
      }

      return typeof value === 'string' ? [value] : [];
    });
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  private normalizePath(value: unknown): string {
    const path = String(value ?? '').split('?')[0].trim().toLowerCase();

    if (!path) {
      return '';
    }

    return path.startsWith('/') ? path : `/${path}`;
  }

  private isActionSlugAllowed(targetSlug: string, actions: UacAction[]): boolean {
    for (const action of actions) {
      // Check action slug
      if (action.slug === targetSlug) {
        return true;
      }

      // Recursively check sub_actions
      if (action.sub_actions && action.sub_actions.length > 0) {
        if (this.isActionSlugAllowed(targetSlug, action.sub_actions)) {
          return true;
        }
      }
    }
    return false;
  }
}
